import React, { useState } from 'react';

import { createLog, LogSeverity } from '@ekhodealer/ekho-common';
import {
	BankInfo,
	Button,
	ToggleCardGroup,
	ToggleCardRadioContent,
} from '@ekhodealer/ekho-common/rev';

import type { BankAccount} from '@ekhodealer/ekho-common';
// Importing React
import type { Key} from 'react';

import './styles.css';

interface BankAccountManagementProps {
	bankAccounts: BankAccount[];
	onSetPrimary: (bankAccountId: string) => Promise<void>;
	handlePlaidLinkOpen: () => void;
	handleClose: () => void;
}

const BankAccountManagement: React.FC<BankAccountManagementProps> = ({
	bankAccounts: incomingBankAccounts,
	onSetPrimary,
	handlePlaidLinkOpen,
	handleClose,
}) => {
	// State to track which account the user has selected (but not yet saved)
	const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);

	// Only reset selection when props change
	React.useEffect(() => {
		setSelectedAccountId(null);
	}, [incomingBankAccounts]);

	const handlePrimaryToggle = (selectedAccountIdSet: Set<Key>) => {
		const newAccountId = selectedAccountIdSet.values().next().value as string;
		setSelectedAccountId(newAccountId);
	};

	const getAccountNumberLastDigits = (accountName: string): string => {
		// Extract last 4 digits if accountName contains them, or return a fallback
		const match = accountName.match(/\d{4}$/);
		return match ? match[0] : '****';
	};

	// Get the currently displayed selected account ID
	const getDisplayedSelectedId = () => {
		if (selectedAccountId !== null) {
			return selectedAccountId;
		}
		return incomingBankAccounts.find((account) => account.primary)?.id || '';
	};

	// Check if the user has selected a different primary account
	const hasChanges = () => {
		const originalPrimaryId = incomingBankAccounts.find((account) => account.primary)?.id;
		const currentSelectedId = getDisplayedSelectedId();

		// If no selection has been made (selectedAccountId is null), no changes
		if (selectedAccountId === null) {
			return false;
		}

		// If the selected account is different from the current primary, there are changes
		return currentSelectedId !== originalPrimaryId;
	};

	const handleSaveChanges = async () => {
		if (!selectedAccountId) {
			createLog('config-mgmt', 'No account selected', LogSeverity.ERROR);
			return;
		}

		try {
			// Close the modal immediately
			handleClose();
			// Then trigger the update
			await onSetPrimary(selectedAccountId);
		} catch (error) {
			createLog(
				'config-mgmt',
				`Failed to update primary account: ${error instanceof Error ? error.message : String(error)}`,
				LogSeverity.ERROR,
			);
			// Note: Modal is already closed, so error handling would need to be
			// handled in the parent component (BankAccountOnboarding)
		}
	};

	// Create a stable key for each account to prevent React from reusing components
	const getAccountKey = (bankAccount: BankAccount) => {
		return `${bankAccount.id}-${bankAccount.primary ? 'primary' : 'secondary'}`;
	};

	return (
		<div>
			<p>
				Only one account can be set as your primary payment method. You can update it
				anytime.
			</p>
			<ToggleCardGroup
				selectionMode='single'
				selectedKeys={new Set([getDisplayedSelectedId()])}
				onSelectionChange={(e) => handlePrimaryToggle(e)}>
				{incomingBankAccounts.map((bankAccount) => (
					<ToggleCardRadioContent
						key={getAccountKey(bankAccount)}
						id={bankAccount.id}
						trailingAccessory={bankAccount.primary ? 'Primary' : undefined}>
						<BankInfo
							bankName={bankAccount.bankName || 'Unknown Bank'}
							accountName={bankAccount.accountName || 'Unknown Account'}
							accountNumberLastDigits={getAccountNumberLastDigits(
								bankAccount.accountName || '',
							)}
						/>
					</ToggleCardRadioContent>
				))}
			</ToggleCardGroup>
			<div>
				<Button
					variant='outline'
					onPress={() => {
						handlePlaidLinkOpen();
						handleClose();
					}}
					className='add-new-bank-account-button'>
					+ Add New
				</Button>
			</div>
			<div className='flex-end-gap'>
				<Button onPress={handleSaveChanges} isDisabled={!hasChanges()}>
					Save and update
				</Button>
			</div>
		</div>
	);
};

export default BankAccountManagement;
